import React, { useEffect, useMemo } from 'react';
import Logo from '../Logo';

const CONFETTI_COUNT = 90;
const CONFETTI_COLORS = [
  '#fbbf24', '#f59e0b', '#fde68a',
  '#e8364f', '#ef4444',
  '#3b82f6', '#60a5fa',
  '#16a34a', '#22c55e',
  '#a855f7', '#ec4899',
];

function buildConfetti() {
  const pieces = [];
  for (let i = 0; i < CONFETTI_COUNT; i++) {
    const duration = 4.5 + Math.random() * 3.5;
    // Spread start times across a full duration (use negative delays so the
    // sky is already full of confetti when the modal opens).
    const delay = -Math.random() * duration;
    pieces.push({
      left: Math.random() * 100,
      delay,
      duration,
      color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      rotate: Math.random() * 360,
      drift: (Math.random() - 0.5) * 240,
      scale: 0.6 + Math.random() * 0.9,
      shape: i % 3,
    });
  }
  return pieces;
}

export default function VictoryPopup({ stats, onNewBattle, onClose, result }) {
  const confetti = useMemo(() => buildConfetti(), []);
  const isForfeit = result === 'forfeit-win';

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="vp-overlay" role="dialog" aria-modal="true" aria-label="Victory">
      <div className="vp-rays" aria-hidden="true" />

      <div className="vp-confetti" aria-hidden="true">
        {confetti.map((c, i) => (
          <span
            key={i}
            className={`vp-confetti-piece shape-${c.shape}`}
            style={{
              left: `${c.left}%`,
              background: c.color,
              animationDelay: `${c.delay}s`,
              animationDuration: `${c.duration}s`,
              '--rot': `${c.rotate}deg`,
              '--drift': `${c.drift}px`,
              '--scale': c.scale,
            }}
          />
        ))}
      </div>

      <div className="vp-card">
        <div className="vp-emblem" aria-hidden="true">
          <span className="vp-emblem-glow" />
          <div className="vp-emblem-logo">
            <Logo size={96} />
          </div>
        </div>

        <p className="vp-kicker">{isForfeit ? 'Opponent Forfeited' : 'Mission Complete'}</p>

        <h1 className="vp-title" aria-label="Victory">
          {[...'VICTORY'].map((ch, i) => (
            <span key={i} className="vp-letter" style={{ '--i': i }}>{ch}</span>
          ))}
        </h1>

        <div className="vp-stats">
          <div className="vp-stat">
            <span className="vp-stat-num">{stats.wins}</span>
            <span className="vp-stat-label">Wins</span>
          </div>
          <div className="vp-stat">
            <span className="vp-stat-num">{stats.losses}</span>
            <span className="vp-stat-label">Losses</span>
          </div>
          <div className="vp-stat">
            <span className="vp-stat-num">{stats.rate}<span className="vp-stat-unit">%</span></span>
            <span className="vp-stat-label">Win Rate</span>
          </div>
          <div className="vp-stat vp-stat-streak">
            <span className="vp-stat-num">
              {stats.streak}
              {stats.streak >= 3 && <span className="vp-stat-flame" aria-hidden="true">🔥</span>}
            </span>
            <span className="vp-stat-label">Streak</span>
          </div>
        </div>

        <div className="vp-actions">
          <button className="vp-btn primary" onClick={onNewBattle}>
            <span className="vp-btn-label">New Battle</span>
            <span className="vp-btn-arrow" aria-hidden="true">→</span>
          </button>
          <button className="vp-btn ghost" onClick={onClose}>Keep Analyzing</button>
        </div>
      </div>
    </div>
  );
}
